import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getNewSupplier, activateSupplier, supplierApplicationAction, updateSupplierContact } from '../../../models/procurementModel';
import { useAuth } from '../../../context/AuthContext';
import { API_BASE_URL } from '../../../config';

function NewSupplierDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [showActivateForm, setShowActivateForm] = useState(false); 
  const [showEditContactForm, setShowEditContactForm] = useState(false);
  const [actionType, setActionType] = useState('');
  const [feedbackData, setFeedbackData] = useState({
    subject: '',
    message: '',
    action: ''
  });
  const [activationData, setActivationData] = useState({
    subject: 'Your registration as a supplier at Silulumanzi has been approved',
    message: ''
  });
  const [contactFormData, setContactFormData] = useState({
    email: '',
    phone: ''
  });
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (id) {
      fetchSupplierDetails();
    }
  }, [id]);

  const fetchSupplierDetails = async () => {
    try {
      setLoading(true);
      const data = await getNewSupplier(id);
      setSupplier(data);
      
      // Set default activation message
      if (data) {
        setActivationData(prev => ({
          ...prev,
          message: `Dear ${data.Contact_Person},

We are pleased to inform you that your application for registration as a supplier at Silulumanzi has been approved.

Your vendor reference number is: ${data.BP_Code}

We look forward to working with you.`
        }));
      }
    } catch (error) {
      console.error('Error fetching supplier details:', error);
      toast.error('Failed to fetch supplier details');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditContact = () => {
    if (supplier) {
      setContactFormData({
        email: supplier.E_Mail || '',
        phone: supplier.Mobile_Phone || ''
      });
      setShowEditContactForm(true);
    }
  };

  const handleUpdateContact = async () => {
    try {
      setProcessing(true);
      
      // Prepare the update data
      const updateData = {
        BP_Code: supplier.BP_Code,
        E_Mail: contactFormData.email,
        Mobile_Phone: contactFormData.phone,
        // Include other required fields to maintain data integrity
        BP_Name: supplier.BP_Name,
        Contact_Person: supplier.Contact_Person,
        Group_Code: supplier.Group_Code || null,
        Company_Reg: supplier.Company_Reg || null,
        BBBEE_Type: supplier.BBBEE_Type || null,
        BBBEE_Level: supplier.BBBEE_Level || null,
        BBBEE_Expiration_Date: supplier.BBBEE_Expiration_Date || null,
        COIDA_Certificate_Number: supplier.COIDA_Certificate_Number || null,
        COIDA_Certificate_Expiry_Date: supplier.COIDA_Certificate_Expiry_Date || null,
        Tax_Certification_Number: supplier.Tax_Certification_Number || null,
        Tax_Certificate_Expiry_Date: supplier.Tax_Certificate_Expiry_Date || null,
        VendorID: supplier.VendorID || null,
        Status: supplier.Status || null
      };

      await updateSupplierContact(supplier.BP_Code, updateData);
      
      // Update local state
      setSupplier(prev => ({
        ...prev,
        E_Mail: contactFormData.email,
        Mobile_Phone: contactFormData.phone
      }));
      
      toast.success('Contact information updated successfully');
      setShowEditContactForm(false);
    } catch (error) {
      console.error('Error updating contact information:', error);
      toast.error(error.response?.data?.message || 'Failed to update contact information');
    } finally {
      setProcessing(false);
    }
  };

  /**
   * Download file with dual support for new standardized and legacy file handling
   * New approach: Direct download via /api/attachments/suppliers/{filename}
   * Legacy approach: POST form to /files/uploads
   */
  const handleDownloadFile = (file) => {
    console.group('🔽 Supplier File Download');
    console.log('File object:', file);
    
    // Detect if this is a new standardized file or legacy file
    // New files will have 'source' field or 'attachment_id' from tb_supplier_attachments
    const isNewFormat = file.source === 'new' || (file.attachment_id && !file.file_id);
    
    console.log('Detection results:', {
      source: file.source,
      attachment_id: file.attachment_id,
      file_id: file.file_id,
      isNewFormat: isNewFormat,
      method: isNewFormat ? 'NEW STANDARDIZED' : 'LEGACY'
    });
    
    if (isNewFormat) {
      // New standardized approach - direct download with cache busting
      const downloadUrl = `${API_BASE_URL}/api/attachments/suppliers/${file.file_path}?v=${Date.now()}`;
      console.log('✅ Using NEW standardized method');
      console.log('Download URL:', downloadUrl);
      console.log('Opening in new tab...');
      window.open(downloadUrl, '_blank');
    } else {
      // Legacy fallback - POST form approach
      console.log('⚠️ Using LEGACY method (POST form)');
      console.log('File path:', file.file_path);
      
      // Use API_BASE_URL to ensure correct path (includes /portal in production)
      const legacyUploadUrl = `${API_BASE_URL}/files/uploads`;
      console.log('Form action:', legacyUploadUrl);
      console.log('Creating hidden form...');
      
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = legacyUploadUrl;
      form.target = '_blank';
      
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'file';
      input.value = file.file_path;
      
      console.log('Form data:', {
        method: form.method,
        action: form.action,
        target: form.target,
        file_param: input.value
      });
      
      form.appendChild(input);
      document.body.appendChild(form);
      console.log('Submitting form...');
      form.submit();
      document.body.removeChild(form);
      console.log('Form submitted and removed from DOM');
    }
    
    console.groupEnd();
  };

  const handleAction = (action, subject) => {
    setActionType(action);
    setFeedbackData({
      subject,
      message: getDefaultMessage(action),
      action
    });
    setShowFeedbackForm(true);
    setShowActivateForm(false);
  };

  const getDefaultMessage = (action) => {
    if (action === '2') {
      return `Dear ${supplier?.Contact_Person},

We would like to request additional information on the following requirements:

• Edit Here

Please login to the supplier portal using the credentials that you created during your registration to submit the requirements.`;
    } else if (action === '7') {
      return `Dear ${supplier?.Contact_Person},

Your registration has been rejected for the following reasons:

• Edit Here`;
    }
    return '';
  };

  const handleSubmitFeedback = async (e) => {
    e.preventDefault();
    try {
      setProcessing(true);
      
      const payload = {
        email: supplier.E_Mail,
        name: supplier.Contact_Person,
        reference: supplier.BP_Code,
        action: feedbackData.action,
        subject: feedbackData.subject,
        message: feedbackData.message
      };

      await supplierApplicationAction(payload);
      toast.success('Feedback sent successfully');
      navigate('/dashboard/procurement/supplier_applications');
    } catch (error) {
      console.error('Error sending feedback:', error);
      
      // Extract the actual error message from the API response
      let errorMessage = 'Failed to send feedback';
      if (error.response && error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  const handleActivateSupplier = async (e) => {
    e.preventDefault();
    try {
      setProcessing(true);
      
      const payload = {
        BP_Code: supplier.BP_Code,
        BP_Name: supplier.BP_Name,
        name: supplier.Contact_Person,
        email: supplier.E_Mail,
        vendor_number: '',
        subject: activationData.subject,
        message: activationData.message
      };

      await activateSupplier(payload);
      toast.success('Supplier activated successfully');
      navigate('/dashboard/procurement/supplier_applications');
    } catch (error) {
      console.error('Error activating supplier:', error);
      
      // Extract the actual error message from the API response
      let errorMessage = 'Failed to activate supplier';
      if (error.response && error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  const parseApplicationData = (jsonString) => {
    try {
      return JSON.parse(jsonString);
    } catch (error) {
      console.error('Error parsing application data:', error);
      return {};
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-2 text-gray-600">Loading supplier details...</span>
      </div>
    );
  }

  if (!supplier) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">Supplier not found</h3>
        <p className="text-gray-500">The requested supplier application could not be found.</p>
      </div>
    );
  }

  const details = parseApplicationData(supplier.application_data || '{}');

  const parseRequiredFiles = () => {
    if (!details.files) {
      return [];
    }

    if (Array.isArray(details.files)) {
      return details.files;
    }

    try {
      const parsed = JSON.parse(details.files);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('Failed to parse required files list:', error);
      return [];
    }
  };

  const normalizeName = (value) => (value || '').toString().trim().toLowerCase();

  const requiredFileEntries = parseRequiredFiles()
    .map((file) => {
      if (typeof file === 'string') {
        const label = file.trim();
        if (!label) {
          return null;
        }
        return { label, key: normalizeName(label) };
      }

      if (file && typeof file === 'object') {
        const label = (file.label || file.name || file.file || '').toString().trim();
        if (!label) {
          return null;
        }
        return { label, key: normalizeName(label) };
      }

      return null;
    })
    .filter(Boolean);

  const attachedFilesRaw = Array.isArray(supplier.files) ? supplier.files : [];

  const normalizedAttachedFiles = attachedFilesRaw.map((file) => {
    const primaryName = (file.file_name || file.display_name || file.original_name || file.name || '').toString();
    const label = primaryName.trim() || 'Document';

    return {
      ...file,
      label,
      normalizedName: normalizeName(primaryName),
    };
  });

  const attachedFileMap = new Map(
    normalizedAttachedFiles
      .filter((file) => file.normalizedName)
      .map((file) => [file.normalizedName, file])
  );

  const additionalAttachments = normalizedAttachedFiles.filter((file) => {
    if (!file.normalizedName) {
      return true;
    }
    return !requiredFileEntries.some((required) => required.key === file.normalizedName);
  });

  return (
    <div>
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard/procurement/supplier_applications')}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Supplier Applications
        </button>
        <h1 className="text-2xl font-semibold text-gray-800">Supplier Application Details</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Application Details */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-6">Application Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-6">
                {/* Company Information - Primary Blue */}
                <div className="border-l-4 pl-4 py-2 rounded-r" style={{ borderColor: 'var(--color-primary)' }}>
                  <label className="block text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--color-primary)' }}>Trading Name</label>
                  <div className="mt-1 text-base font-medium text-gray-900">{supplier.BP_Name || <span className="text-gray-400 italic">N/A</span>}</div>
                </div>

                <div className="border-l-4 pl-4 py-2 rounded-r" style={{ borderColor: 'var(--color-primary)' }}>
                  <label className="block text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--color-primary)' }}>Registered Company Name</label>
                  <div className="mt-1 text-base font-medium text-gray-900">{supplier.Company_Registered_Name || <span className="text-gray-400 italic">N/A</span>}</div>
                </div>

                <div className="border-l-4 pl-4 py-2 rounded-r" style={{ borderColor: 'var(--color-primary)' }}>
                  <label className="block text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--color-primary)' }}>Holding Company Name</label>
                  <div className="mt-1 text-base font-medium text-gray-900">{supplier.Holding_Company_Name || <span className="text-gray-400 italic">N/A</span>}</div>
                </div>

                {/* Registration Information - Secondary Green */}
                <div className="border-l-4 pl-4 py-2 rounded-r" style={{ borderColor: 'var(--color-secondary)' }}>
                  <label className="block text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--color-secondary)' }}>Company Registration</label>
                  <div className="mt-1 text-base font-medium text-gray-900">{supplier.Company_Reg || <span className="text-gray-400 italic">N/A</span>}</div>
                </div>

                <div className="border-l-4 pl-4 py-2 rounded-r" style={{ borderColor: 'var(--color-secondary)' }}>
                  <label className="block text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--color-secondary)' }}>VAT Registration</label>
                  <div className="mt-1 text-base font-medium text-gray-900">{supplier.Vat_Registration || <span className="text-gray-400 italic">N/A</span>}</div>
                </div>

                {/* BBBEE Information - Success Green */}
                <div className="border-l-4 pl-4 py-2 rounded-r" style={{ borderColor: 'var(--color-success)' }}>
                  <label className="block text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--color-success)' }}>BBBEE Type</label>
                  <div className="mt-1 text-base font-medium text-gray-900">{supplier.BBBEE_Type || <span className="text-gray-400 italic">N/A</span>}</div>
                </div>

                <div className="border-l-4 pl-4 py-2 rounded-r" style={{ borderColor: 'var(--color-success)' }}>
                  <label className="block text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--color-success)' }}>BBBEE Level</label>
                  <div className="mt-1 text-base font-medium text-gray-900">{supplier.BBBEE_Level || <span className="text-gray-400 italic">N/A</span>}</div>
                </div>

                <div className="border-l-4 pl-4 py-2 rounded-r" style={{ borderColor: 'var(--color-success)' }}>
                  <label className="block text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--color-success)' }}>BBBEE Expiration Date</label>
                  <div className="mt-1 text-base font-medium text-gray-900">{supplier.BBBEE_Expiration_Date || <span className="text-gray-400 italic">N/A</span>}</div>
                </div>

                {/* COIDA Information - Info Blue */}
                <div className="border-l-4 pl-4 py-2 rounded-r" style={{ borderColor: 'var(--color-info)' }}>
                  <label className="block text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--color-info)' }}>COIDA Certificate Number</label>
                  <div className="mt-1 text-base font-medium text-gray-900">{supplier.COIDA_Certificate_Number || <span className="text-gray-400 italic">N/A</span>}</div>
                </div>

                <div className="border-l-4 pl-4 py-2 rounded-r" style={{ borderColor: 'var(--color-info)' }}>
                  <label className="block text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--color-info)' }}>COIDA Expiry Date</label>
                  <div className="mt-1 text-base font-medium text-gray-900">{supplier.COIDA_Certificate_Expiry_Date || <span className="text-gray-400 italic">N/A</span>}</div>
                </div>

                {/* Tax Information - Warning Orange */}
                <div className="border-l-4 pl-4 py-2 rounded-r" style={{ borderColor: 'var(--color-warning)' }}>
                  <label className="block text-xs font-bold uppercase tracking-wide" style={{ color: '#d4740e' }}>Tax Certification Number</label>
                  <div className="mt-1 text-base font-medium text-gray-900">{supplier.Tax_Certification_Number || <span className="text-gray-400 italic">N/A</span>}</div>
                </div>

                <div className="border-l-4 pl-4 py-2 rounded-r" style={{ borderColor: 'var(--color-warning)' }}>
                  <label className="block text-xs font-bold uppercase tracking-wide" style={{ color: '#d4740e' }}>Tax Certificate Expiry Date</label>
                  <div className="mt-1 text-base font-medium text-gray-900">{supplier.Tax_Certificate_Expiry_Date || <span className="text-gray-400 italic">N/A</span>}</div>
                </div>
              </div>

              <div className="space-y-6">
                {/* Location Information - Accent Blue */}
                <div className="border-l-4 pl-4 py-2 rounded-r" style={{ borderColor: 'var(--color-accent)' }}>
                  <label className="block text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--color-accent)' }}>Address</label>
                  <div className="mt-1 text-base font-medium text-gray-900">{supplier.Address || <span className="text-gray-400 italic">N/A</span>}</div>
                </div>

                <div className="border-l-4 pl-4 py-2 rounded-r" style={{ borderColor: 'var(--color-accent)' }}>
                  <label className="block text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--color-accent)' }}>City</label>
                  <div className="mt-1 text-base font-medium text-gray-900">{supplier.City || <span className="text-gray-400 italic">N/A</span>}</div>
                </div>

                <div className="border-l-4 pl-4 py-2 rounded-r" style={{ borderColor: 'var(--color-accent)' }}>
                  <label className="block text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--color-accent)' }}>Country</label>
                  <div className="mt-1 text-base font-medium text-gray-900">{supplier.Country || <span className="text-gray-400 italic">N/A</span>}</div>
                </div>

                <div className="border-l-4 pl-4 py-2 rounded-r" style={{ borderColor: 'var(--color-accent)' }}>
                  <label className="block text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--color-accent)' }}>Site/Branch</label>
                  <div className="mt-1 text-base font-medium text-gray-900">{supplier.Supplier_Site || <span className="text-gray-400 italic">N/A</span>}</div>
                </div>

                {/* Contact Information - Muted Gray */}
                <div className="border-l-4 pl-4 py-2 rounded-r" style={{ borderColor: 'var(--color-muted)' }}>
                  <label className="block text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--text-secondary)' }}>Preferred Correspondence Method</label>
                  <div className="mt-1 text-base font-medium text-gray-900">{supplier.Preferred_Correspondence_Method || <span className="text-gray-400 italic">N/A</span>}</div>
                </div>

                <div className="border-l-4 pl-4 py-2 rounded-r" style={{ borderColor: 'var(--color-muted)' }}>
                  <label className="block text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--text-secondary)' }}>Contact Person</label>
                  <div className="mt-1 text-base font-medium text-gray-900">{supplier.Contact_Person || <span className="text-gray-400 italic">N/A</span>}</div>
                </div>

                <div className="border-l-4 pl-4 py-2 rounded-r" style={{ borderColor: 'var(--color-muted)' }}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <label className="block text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--text-secondary)' }}>Email</label>
                      <div className="mt-1 text-base font-medium text-gray-900">{supplier.E_Mail || <span className="text-gray-400 italic">N/A</span>}</div>
                    </div>
                    {can('Procurement') && (
                      <button
                        onClick={handleOpenEditContact}
                        className="ml-4 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium transition-colors"
                        title="Edit contact information"
                      >
                        Edit
                      </button>
                    )}
                  </div>
                </div>

                <div className="border-l-4 pl-4 py-2 rounded-r" style={{ borderColor: 'var(--color-muted)' }}>
                  <label className="block text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--text-secondary)' }}>Mobile Phone</label>
                  <div className="mt-1 text-base font-medium text-gray-900">{supplier.Mobile_Phone || <span className="text-gray-400 italic">N/A</span>}</div>
                </div>

                <div className="border-l-4 pl-4 py-2 rounded-r" style={{ borderColor: 'var(--color-muted)' }}>
                  <label className="block text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--text-secondary)' }}>Telephone</label>
                  <div className="mt-1 text-base font-medium text-gray-900">{supplier.Tel || <span className="text-gray-400 italic">N/A</span>}</div>
                </div>

                {/* Financial Information - Active Orange */}
                <div className="border-l-4 pl-4 py-2 rounded-r" style={{ borderColor: 'var(--status-active)' }}>
                  <label className="block text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--status-active)' }}>Settlement Discount</label>
                  <div className="mt-1 text-base font-medium text-gray-900">{supplier.Settlement_Discount || <span className="text-gray-400 italic">N/A</span>}</div>
                </div>

                <div className="border-l-4 pl-4 py-2 rounded-r" style={{ borderColor: 'var(--status-active)' }}>
                  <label className="block text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--status-active)' }}>Settlement Discount Percentage</label>
                  <div className="mt-1 text-base font-medium text-gray-900">{supplier.Settlement_Discount_Percentage || <span className="text-gray-400 italic">N/A</span>}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Services Section */}
          {supplier.Group_Code && (
            <div className="bg-white rounded-lg shadow-md p-6 mt-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Selected Services</h2>
              <div className="flex flex-wrap gap-2">
                {supplier.Group_Code.split(',').map((code, idx) => {
                  const trimmedCode = code.trim();
                  return trimmedCode ? (
                    <span 
                      key={idx} 
                      className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {trimmedCode}
                    </span>
                  ) : null;
                })}
              </div>
              {!supplier.Group_Code.trim() && (
                <p className="text-gray-400 italic text-sm">No services specified</p>
              )}
            </div>
          )}
        </div>

        {/* Declaration and Actions Sidebar */}
        <div className="space-y-6">
          {/* Declaration */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Declaration</h2>
            <div className="space-y-4 text-sm">
              {details.Declare_Silulumanzi_Employee_Text && (
                <div>
                  <div className="bg-yellow-100 text-yellow-800 p-2 rounded text-xs mb-1">
                    {details.Declare_Silulumanzi_Employee_Text}
                  </div>
                  <div className="text-gray-900">{details.Declare_Silulumanzi_Employee}</div>
                  {details.Declare_Silulumanzi_Employee_Details && (
                    <div className="text-gray-600 mt-1">{details.Declare_Silulumanzi_Employee_Details}</div>
                  )}
                </div>
              )}

              {details.Declare_Silulumanzi_Recent_Employee_Text && (
                <div>
                  <div className="bg-yellow-100 text-yellow-800 p-2 rounded text-xs mb-1">
                    {details.Declare_Silulumanzi_Recent_Employee_Text}
                  </div>
                  <div className="text-gray-900">{details.Declare_Silulumanzi_Recent_Employee}</div>
                  {details.Declare_Silulumanzi_Recent_Employee_Details && (
                    <div className="text-gray-600 mt-1">{details.Declare_Silulumanzi_Recent_Employee_Details}</div>
                  )}
                </div>
              )}

              {details.Declare_Silulumanzi_Employee_Relative_Text && (
                <div>
                  <div className="bg-yellow-100 text-yellow-800 p-2 rounded text-xs mb-1">
                    {details.Declare_Silulumanzi_Employee_Relative_Text}
                  </div>
                  <div className="text-gray-900">{details.Declare_Silulumanzi_Employee_Relative}</div>
                  {details.Declare_Silulumanzi_Employee_Relative_Details && (
                    <div className="text-gray-600 mt-1">{details.Declare_Silulumanzi_Employee_Relative_Details}</div>
                  )}
                </div>
              )}
            </div>

            <h3 className="text-md font-medium text-gray-900 mt-6 mb-3">Quality, Safety and Environment</h3>
            <div className="space-y-4 text-sm">
              {details.Safety_Environmental_Policy_Text && (
                <div>
                  <div className="bg-blue-100 text-blue-800 p-2 rounded text-xs mb-1">
                    {details.Safety_Environmental_Policy_Text}
                  </div>
                  <div className="text-gray-900">{details.Safety_Environmental_Policy}</div>
                </div>
              )}

              {details.Safety_Hazardous_Substances_Text && (
                <div>
                  <div className="bg-blue-100 text-blue-800 p-2 rounded text-xs mb-1">
                    {details.Safety_Hazardous_Substances_Text}
                  </div>
                  <div className="text-gray-900">{details.Safety_Hazardous_Substances}</div>
                  {details.Safety_Hazardous_Substances_Details && (
                    <div className="text-gray-600 mt-1">{details.Safety_Hazardous_Substances_Details}</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Required Attachments */}
      <div className="mt-6 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Required Attachments</h2>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Document
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Attached
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {requiredFileEntries.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-6 py-4 text-sm text-gray-500 italic">
                    No specific required documents were configured for this application.
                  </td>
                </tr>
              ) : (
                requiredFileEntries.map(({ label, key }, index) => {
                  let file = key ? attachedFileMap.get(key) : undefined;

                  if (!file) {
                    file = normalizedAttachedFiles.find((attached) => normalizeName(attached.label) === normalizeName(label));
                  }

                  const isAttached = Boolean(file);

                  return (
                    <tr key={`${label}-${index}`}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {label}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                        {isAttached ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Yes
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            No
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {file && (
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleDownloadFile(file)}
                              className="text-blue-600 hover:text-blue-900"
                              title={file.source === 'legacy' ? 'Legacy file - uses old download method' : 'Download file'}
                            >
                              Download
                            </button>
                            {file.source === 'legacy' && (
                              <span className="text-xs text-gray-400 italic" title="This file uses the legacy storage system">
                                (Legacy)
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {additionalAttachments.length > 0 && (
          <div className="mt-6">
            <h3 className="text-md font-medium text-gray-900 mb-3">Additional Attachments</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <tbody className="bg-white divide-y divide-gray-200">
                  {additionalAttachments.map((file, index) => (
                    <tr key={`${file.label}-${index}`}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {file.label}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Yes
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleDownloadFile(file)}
                            className="text-blue-600 hover:text-blue-900"
                            title={file.source === 'legacy' ? 'Legacy file - uses old download method' : 'Download file'}
                          >
                            Download
                          </button>
                          {file.source === 'legacy' && (
                            <span className="text-xs text-gray-400 italic" title="This file uses the legacy storage system">
                              (Legacy)
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="mt-6 flex flex-wrap gap-3">
          <button
            onClick={() => {
              setShowActivateForm(true);
              setShowFeedbackForm(false);
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            Activate
          </button>
          
          <button
            onClick={() => handleAction('2', 'New Supplier Registration - More Information Required')}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Request More Information
          </button>
          
          <button
            onClick={() => handleAction('7', 'New Supplier Registration - Application Rejected')}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            Reject Application
          </button>
 
        </div>
      </div>

      {/* Feedback Form Modal */}
      {showFeedbackForm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-2xl bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
            {/* Modal Header */}
            <div className="px-8 py-6 border-b border-gray-200 rounded-t-xl" style={{ background: 'linear-gradient(135deg, var(--color-primary), var(--color-accent))' }}>
              <h3 className="text-xl font-bold text-white flex items-center">
                <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                        d={actionType === '2' ? "M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" : "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 15.5c-.77.833.192 2.5 1.732 2.5z"} />
                </svg>
                {actionType === '2' ? 'Request More Information' : 'Reject Application'}
              </h3>
              <p className="text-blue-100 text-sm mt-1">
                {actionType === '2' ? 'Send a request for additional information from the supplier' : 'Provide feedback for the application rejection'}
              </p>
            </div>
            
            {/* Modal Body */}
            <div className="px-8 py-6">
              <form onSubmit={handleSubmitFeedback} className="space-y-6">
                <div>
                  <label htmlFor="subject" className="block text-sm font-bold text-gray-800 mb-2">
                    Subject <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="subject"
                    value={feedbackData.subject}
                    onChange={(e) => setFeedbackData(prev => ({ ...prev, subject: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder-gray-500 text-base"
                    placeholder="Enter a clear and descriptive subject line"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-bold text-gray-800 mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    rows={8}
                    value={feedbackData.message}
                    onChange={(e) => setFeedbackData(prev => ({ ...prev, message: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder-gray-500 text-base leading-relaxed resize-none"
                    placeholder={actionType === '2' ? 'Please specify what additional information or documents are required...' : 'Please provide clear feedback about why this application is being rejected...'}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-2 flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Be specific and professional in your communication
                  </p>
                </div>

                {/* Modal Footer */}
                <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowFeedbackForm(false)}
                    className="px-6 py-3 border border-gray-300 rounded-lg text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={processing}
                    className="px-6 py-3 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center"
                  >
                    {processing ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </>
                    ) : (
                      'Send Message'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Activation Form Modal */}
      {showActivateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-2xl bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
            {/* Modal Header */}
            <div className="px-8 py-6 border-b border-gray-200 rounded-t-xl" style={{ background: 'linear-gradient(135deg, var(--color-success), var(--color-success-bright))' }}>
              <h3 className="text-xl font-bold text-white flex items-center">
                <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Activate Supplier
              </h3>
              <p className="text-green-100 text-sm mt-1">
                Approve this supplier application and send activation notification
              </p>
            </div>
            
            {/* Modal Body */}
            <div className="px-8 py-6">
              <form onSubmit={handleActivateSupplier} className="space-y-6">
                <div>
                  <label htmlFor="activateSubject" className="block text-sm font-bold text-gray-800 mb-2">
                    Subject <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="activateSubject"
                    value={activationData.subject}
                    onChange={(e) => setActivationData(prev => ({ ...prev, subject: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 text-gray-900 placeholder-gray-500 text-base"
                    placeholder="Email subject line for the supplier"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="activateMessage" className="block text-sm font-bold text-gray-800 mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="activateMessage"
                    rows={8}
                    value={activationData.message}
                    onChange={(e) => setActivationData(prev => ({ ...prev, message: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 text-gray-900 placeholder-gray-500 text-base leading-relaxed resize-none"
                    placeholder="Welcome message and next steps for the approved supplier..."
                    required
                  />
                  <p className="text-xs text-gray-500 mt-2 flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    This message will be sent to the supplier via email
                  </p>
                </div>

                {/* Modal Footer */}
                <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowActivateForm(false)}
                    className="px-6 py-3 border border-gray-300 rounded-lg text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={processing}
                    className="px-6 py-3 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center"
                  >
                    {processing ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Activating...
                      </>
                    ) : (
                      'Activate Supplier'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showEditContactForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full">
            <div className="border-b px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">Edit Contact Information</h3>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleUpdateContact();
              }}
              className="p-6 space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={contactFormData.email}
                  onChange={(e) => setContactFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="example@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mobile Phone
                </label>
                <input
                  type="tel"
                  value={contactFormData.phone}
                  onChange={(e) => setContactFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="+27 12 345 6789"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditContactForm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={processing}
                  className="flex-1 px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center"
                >
                  {processing ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
 
    </div>
  );
}

 

export default NewSupplierDetails;
